import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import {
  TABLE_SORTER_TOOLTIP,
  LIST_SURFACE_STYLE,
  ENTITY_NAME_LINK_STYLE,
  MONO_NUMERIC_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { getDealStageTone, getEnumLabel } from '../../../shared/format/enum-labels.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table, Typography, Button, Space, Tooltip } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { ArrowRightOutlined, EditOutlined } from '@ant-design/icons';

import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import type { GridColumnMeta, GridProfile } from '../../../shared/grid/useGridProfile.js';
import { useOrderedColumns } from '../../../shared/grid/useOrderedColumns.js';
import {
  formatDateTime,
  formatMoney,
  formatPersonName,
  isPastDue,
} from '../../../shared/format/index.js';
import { EntityHistoryButton } from '../../activity/components/EntityHistoryButton.js';
import { LinkCell, linkSortLabel, type OwnLink } from '../../../shared/ui/LinkCell.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { OverdueDate, overdueRowStyle } from '../../../shared/ui/OverdueDate.js';
import { byDate, byNumber, byOrder, byText } from '../../../shared/ui/tableSorters.js';
import { STAGE_ORDER } from '../lib/kanban.js';
import type { DealListItem, DealStage } from '../api/deals.client.js';

const { Text } = Typography;

const NEXT_STAGE: Partial<Record<DealStage, DealStage>> = {
  New: 'Contacted',
  Contacted: 'Negotiation',
  Negotiation: 'Won',
};

export const DEAL_GRID_COLUMNS: GridColumnMeta[] = [
  {
    key: 'title',
    get label() {
      return i18n.t('crm.features.map.features.deals.components.deallistview.39');
    },
  },
  {
    key: 'company',
    get label() {
      return i18n.t('crm.features.map.features.deals.components.deallistview.40');
    },
  },
  {
    key: 'contact',
    get label() {
      return i18n.t('crm.features.map.features.deals.components.deallistview.41');
    },
  },
  {
    key: 'stage',
    get label() {
      return i18n.t('crm.features.map.features.deals.components.deallistview.42');
    },
  },
  {
    key: 'amount',
    get label() {
      return i18n.t('crm.features.map.features.deals.components.deallistview.43');
    },
  },
  {
    key: 'nextStep',
    get label() {
      return i18n.t('crm.features.map.features.deals.components.deallistview.44');
    },
  },
  {
    key: 'action',
    get label() {
      return i18n.t('crm.features.map.features.deals.components.deallistview.45');
    },
  },
  {
    key: 'createdAt',
    get label() {
      return i18n.t('crm.features.map.features.deals.components.deallistview.46');
    },
    hiddenByDefault: true,
  },
];

export interface DealListViewProps {
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  deals: DealListItem[];
  selectedIds: ReadonlySet<string>;
  isUpdating: boolean;
  onMoveDeal: (dealId: string, toStage: DealStage) => void;
  /** Единая форма выбора строк: грид отдаёт весь набор ключей, как у остальных. */
  onSelectionChange: (dealIds: string[]) => void;
  /** Правка и удаление прямо из строки — как в гридах задач, заметок и встреч. */
  onEdit: (deal: DealListItem) => void;
  onDelete: (deal: DealListItem) => void | Promise<unknown>;
  deletePendingId?: string | null;
  /** Вид грида; хук живёт на странице — там же кнопка «Колонки». */
  profile: GridProfile;
}

function getContactName(deal: DealListItem): string {
  return formatPersonName(
    deal.contact?.firstName,
    deal.contact?.lastName,
    i18n.t('crm.features.features.deals.components.deallistview.контакт_не_указан'),
  );
}

/**
 * Своя компания сделки, приведённая к общей форме ячейки.
 *
 * Наследование («компания контакта, если у сделки своей нет») считает сервер и
 * присылает в `inheritedLinks`. Здесь его когда-то считали руками — это была
 * третья копия правила, и в ней, например, не было мягко удалённого контакта:
 * удалённый втягивал сделку в выдачу фирмы, а фильтр её уже не показывал.
 */
function ownCompany(deal: DealListItem): OwnLink | null {
  return deal.company ? { id: deal.company.id, label: deal.company.name } : null;
}

export function DealListView({
  deals,
  selectedIds,
  isUpdating,
  onMoveDeal,
  onSelectionChange,
  onEdit,
  onDelete,
  deletePendingId = null,
  profile: grid,
  loading = false,
  error = null,
  onRetry,
}: DealListViewProps): ReactElement {
  useI18nSubscription();
  const location = useLocation();
  const pagination = useDataViewPagination(deals);
  const columnMap: Record<string, ColumnType<DealListItem>> = {
    title: {
      title: i18n.t('crm.features.features.deals.components.deallistview.сделка'),
      dataIndex: 'title',
      key: 'title',
      sorter: byText<DealListItem>((row) => row.title),
      render: (text: string, record) => (
        <Link
          to={`/deals/${record.id}`}
          state={{ dealListUrl: location.pathname + location.search }}
          style={ENTITY_NAME_LINK_STYLE}
        >
          {text}
        </Link>
      ),
    },
    company: {
      title: i18n.t('crm.features.features.deals.components.deallistview.компания'),
      key: 'company',
      // Сделки без компании уходят вниз — как и в колонке контакта. Сортируем по
      // показанному значению, включая унаследованное.
      sorter: byText<DealListItem>((row) =>
        linkSortLabel(ownCompany(row), row.inheritedLinks?.company),
      ),
      // Своя компания — фирменной ссылкой, как название сделки. Унаследованная —
      // приглушённо: это не то, что проставили у сделки, и разницу должно быть видно
      // с одного взгляда. Считает наследование сервер, а не эта колонка.
      render: (_, record) => (
        <LinkCell
          kind="company"
          own={ownCompany(record)}
          inherited={record.inheritedLinks?.company}
          emptyText={i18n.t('crm.features.features.deals.components.deallistview.без_компании')}
        />
      ),
    },
    contact: {
      title: i18n.t('crm.features.features.deals.components.deallistview.контакт'),
      key: 'contact',
      // Сделки без контакта уходят вниз, а не собираются под заглушкой в начале.
      sorter: byText<DealListItem>((row) =>
        row.contact ? formatPersonName(row.contact.firstName, row.contact.lastName) : null,
      ),
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {getContactName(record)}
        </Text>
      ),
    },
    stage: {
      title: i18n.t('crm.features.features.deals.components.deallistview.стадия'),
      dataIndex: 'stage',
      key: 'stage',
      align: 'center',
      // Порядок воронки, а не алфавит: Won не должен идти перед New.
      sorter: byOrder<DealListItem, DealStage>((row) => row.stage, STAGE_ORDER),
      render: (stage: string) => (
        <EntityStatusChip tone={getDealStageTone(stage)} style={{ margin: 0 }}>
          {getEnumLabel('dealStage', stage, i18n.t)}
        </EntityStatusChip>
      ),
    },
    amount: {
      title: i18n.t('crm.features.features.deals.components.deallistview.сумма'),
      dataIndex: 'amount',
      key: 'amount',
      sorter: byNumber<DealListItem>((row) => row.amount),
      render: (amount: string) => (
        <Text
          style={{
            fontWeight: 600,
            ...MONO_NUMERIC_STYLE,
            color: 'var(--ant-color-text-secondary)',
          }}
        >
          {formatMoney(amount)}
        </Text>
      ),
    },
    nextStep: {
      title: i18n.t('crm.features.features.deals.components.deallistview.следующий_шаг'),
      key: 'nextStep',
      sorter: byDate<DealListItem>((row) => row.nextActivityDue),
      render: (_, record) => (
        <OverdueDate
          iso={record.nextActivityDue}
          title={i18n.t(
            'crm.features.features.deals.components.deallistview.срок_следующего_шага_прошел',
          )}
        />
      ),
    },
    createdAt: {
      title: i18n.t('crm.features.features.deals.components.deallistview.дата_создания'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: byDate<DealListItem>((row) => row.createdAt),
      render: (value: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateTime(value)}
        </Text>
      ),
    },
    action: {
      title: i18n.t('crm.features.features.deals.components.deallistview.действие'),
      key: 'action',
      align: 'center',
      render: (_, record) => {
        const nextStage = NEXT_STAGE[record.stage];
        if (nextStage !== undefined) {
          return (
            <Button
              size="small"
              aria-label={i18n.t('crm.features.dynamic.features.deals.components.deallistview.54', {
                p0: record.title,
              })}
              disabled={isUpdating}
              onClick={(e) => {
                e.stopPropagation();
                onMoveDeal(record.id, nextStage);
              }}
              icon={<ArrowRightOutlined />}
            >
              {getEnumLabel('dealStage', nextStage, i18n.t)}
            </Button>
          );
        }
        return (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {i18n.t('crm.features.features.deals.components.deallistview.финальная')}
          </Text>
        );
      },
    },
  };

  const actionsColumn: ColumnType<DealListItem> = {
    title: i18n.t('crm.features.features.deals.components.deallistview.действия'),
    key: 'actions',
    align: 'center',
    width: 110,
    render: (_, record) => (
      <Space size="small">
        <Tooltip
          title={i18n.t('crm.features.features.deals.components.deallistview.редактировать')}
        >
          <Button
            size="small"
            type="text"
            aria-label={i18n.t('crm.features.dynamic.features.deals.components.deallistview.55', {
              p0: record.title,
            })}
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(record);
            }}
          />
        </Tooltip>
        <EntityHistoryButton
          entity="deal"
          entityId={record.id}
          title={i18n.t('crm.features.features.deals.components.deallistview.история_сделки')}
          subject={record.title}
          iconOnly
        />
        <DeleteEntityButton
          iconOnly
          label={i18n.t('crm.features.features.deals.components.deallistview.удалить_сделку')}
          title={i18n.t('crm.features.features.deals.components.deallistview.удалить_сделку_2')}
          description={i18n.t(
            'crm.features.features.deals.components.deallistview.задачи_заметки_и_встречи_сделки_останутся_',
          )}
          loading={deletePendingId === record.id}
          onConfirm={() => onDelete(record)}
        />
      </Space>
    ),
  };

  const ordered = useOrderedColumns<DealListItem>({
    profile: grid,
    columnMap,
    trailing: [actionsColumn],
  });

  return (
    <div className="crm-checkbox-square" style={LIST_SURFACE_STYLE} data-testid="deals-list-view">
      {loading ? (
        <QuietListSkeleton rows={6} />
      ) : error ? (
        <div className="p-4">
          <ErrorState
            title={i18n.t(
              'crm.features.features.deals.pages.dealkanbanpage.не_удалось_загрузить_сделки',
            )}
            error={error}
            {...(onRetry ? { onRetry } : {})}
          />
        </div>
      ) : (
        <Table<DealListItem>
          showSorterTooltip={TABLE_SORTER_TOOLTIP}
          rowSelection={{
            columnWidth: 48,
            preserveSelectedRowKeys: true,
            selectedRowKeys: Array.from(selectedIds),
            onChange: (keys) => onSelectionChange(keys.map(String)),
            getCheckboxProps: (record) => ({
              title: i18n.t('crm.features.dynamic.features.deals.components.deallistview.56', {
                p0: record.title,
              }),
              'aria-label': i18n.t(
                'crm.features.dynamic.features.deals.components.deallistview.57',
                {
                  p0: record.title,
                },
              ),
            }),
          }}
          columns={ordered.columns}
          components={ordered.components}
          dataSource={deals}
          locale={{ emptyText: <QuietEmpty text={i18n.t('crm.entity_detail.empty_deals')} /> }}
          rowKey="id"
          size="small"
          pagination={pagination.pagination}
          style={{ fontSize: 13 }}
          onRow={(record) => overdueRowStyle(isPastDue(record.nextActivityDue))}
        />
      )}
    </div>
  );
}
